// React and React Router
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

// Material UI Core Components and Icons
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  makeStyles,
  AppBar,
  Toolbar,
  Divider,
} from "@material-ui/core";
import { Description, Edit, Note, ViewAgenda } from "@material-ui/icons";

// Custom Components
import UploadSourceFile from "./UploadSourceFile";
import LabDesignStep from "./LabDesignStep";
import LabIdea from "./LabIdea";
import LabUseCase from "./LabUseCase";
import NotebookReview from "./NotebookReview";
import ReviewLabProject from "../ReviewLabProject";

// API Utilities
import { fetchLabAPI } from "views/APIs/APIUtility";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { stepperStyles, QontoConnector } from "views/Styles/stepperStyles";
import appbarStyles from "views/Styles/appbarStyles";

// Contexts
import NotificationContext from "views/Components/Context.js";
import { Auth } from "aws-amplify";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";
import { updateLabDetails } from "views/APIs/APIUtility";

const useStyles = makeStyles((theme) => ({
  ...styles,
  ...stepperStyles(theme),
  ...appbarStyles(theme),
}));

const LoadingStepIcon = () => <CircularProgress size={16} />;

export default function LabsBoard(props) {
  const history = useHistory();
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);

  const [labSteps, setLabSteps] = useState([
    { label: "Lab Definition", icon: <Description />, lockedMessage: "" },
    {
      label: "Idea Selection",
      icon: <ViewAgenda />,
      lockedMessage:
        "Please review the lab design to move on to the next step.",
    },
    {
      label: "Lab Design",
      icon: <ViewAgenda />,
      lockedMessage:
        "Please upload at least one resource file to move on to the next step.",
    },
    {
      label: "Notebook Specifications",
      icon: <Edit />,
      lockedMessage: "Please review the ideas to move on to the next step.",
    },
    {
      label: "Notebook Review",
      icon: <Note />,
      lockedMessage:
        "Please review and submit the previous step to move on to the next step.",
    },
    {
      label: "Streamlit Lab Design",
      icon: <ViewAgenda />,
      lockedMessage:
        "Please review the notebook to move on to the next step.",
    },
    {
      label: "Streamlit Specifications",
      icon: <Edit />,
      lockedMessage: "Please review the streamlit lab design to move on to the next step.",
    },
    {
      label: "Final Deliverables",
      icon: <Note />,
      lockedMessage:
        "Please review and submit the previous step to move on to the next step.",
    },
  ]);

  const [statusToStepMapping, setStatusToStepMapping] = useState({
    "In Design Phase": 0,
    "Idea Selection": 1,
    "Lab Design": 2,
    "Technical Specifications": 3,
    "QuResearch Specifications": 3,
    "In Notebook Generation Queue": 3,
    "Notebook Generation Failed": 3,
    "Notebook Specifications": 3,
    "Notebook Specifications Review": 3,
    "Notebook Review": 4,
    "Streamlit Lab Design": 5,
    "In Lab Generation Queue": 6,
    "Lab Generation Failed": 6,
    "Lab Specifications": 6,
    "Streamlit Specifications Review": 6,
    "Streamlit Specifications": 6,
    Review: 7,
    Published: 7,
    default: 0,
  });

  // State variables
  const [contentStep, setContentStep] = useState(0); // drives content
  const [labCompletedTillStep, setLabCompletedTillStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState([0]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [labName, setLabName] = useState("");
  const [lab, setLab] = useState({});
  const [labDetails, setLabDetails] = useState({});
  const [labGeneration, setLabGeneration] = useState(false);

  const { labID } = props.match.params;

  // In-between loader messages (LOCAL)
  const inBetweenStagesLoaderOptions = {
    "In Lab Generation Queue": "Creating interactive hands-on lab...",
    "In Notebook Generation Queue": "Composing low-code Jupyter notebook...",
    "In Streamlit Generation Queue": "Composing interactive Streamlit app...",
  };
  const [
    inBetweenStagesLoaderMessage,
    setInBetweenStagesLoaderMessage,
  ] = useState("Loading...");

  // Auth user
  const [user, setUser] = useState("");

  // Only suppress the **content** loader after user clicks back; Stepper stays in loading
  const [suppressContentOnly, setSuppressContentOnly] = useState(false);

  // Derived flags
  const isInBetween = useMemo(
    () =>
      Object.prototype.hasOwnProperty.call(
        inBetweenStagesLoaderOptions,
        lab.status
      ),
    [lab.status]
  );

  // Big expected-time line
  const WAIT_MSG = useMemo(() => {
    if (!isInBetween) return "";
    const min = 10 + Math.floor(Math.random() * 5);
    const max = 15 + Math.floor(Math.random() * 6);
    return `Expected time: ${min}-${max} minutes. You can leave this tab; processing will continue in the background.`;
  }, [isInBetween]);

  // Step index from current status (base)
  const baseStepIndex = useMemo(() => {
    const raw = statusToStepMapping[lab.status];
    return Number.isInteger(raw) ? raw : statusToStepMapping.default;
  }, [lab.status, statusToStepMapping]);

  // Stepper behavior
  const stepperInBetween = isInBetween; // stepper always shows loading when in-between
  const contentInBetween = isInBetween && !suppressContentOnly; // content can hide loader after back nav

  // Visual step for the Stepper (pin to loading bubble if in-between)
  const visualActiveStep = useMemo(
    () => (stepperInBetween ? baseStepIndex + 1 : contentStep),
    [stepperInBetween, baseStepIndex, contentStep]
  );

  // Build steps with an inserted Loading… pseudo-step
  const stepsWithIntermediate = useMemo(() => {
    if (!stepperInBetween) return labSteps;
    const insertAt = baseStepIndex + 1;
    const loadingStep = {
      label: "Loading…",
      isIntermediate: true,
      lockedMessage: "",
    };
    return [
      ...labSteps.slice(0, insertAt),
      loadingStep,
      ...labSteps.slice(insertAt),
    ];
  }, [stepperInBetween, baseStepIndex, labSteps]);

  // Keep the small stage message updated
  useEffect(() => {
    const msg = inBetweenStagesLoaderOptions[lab.status] || "Loading...";
    setInBetweenStagesLoaderMessage(msg);
  }, [lab.status]);

  // Update URL stage param and visited steps when contentStep changes
  const setStage = useCallback(
    (newStage) => {
      const current = new URLSearchParams(history.location.search).get("stage");
      if (String(newStage) !== current) {
        history.push(`?stage=${newStage}`);
      }
    },
    [history]
  );

  useEffect(() => {
    setStage(contentStep);
    setVisitedSteps((prev) =>
      prev.includes(contentStep) ? prev : [...prev, contentStep]
    );
  }, [contentStep, setStage]);

  // Fetch lab details on mount / labID change
  useEffect(() => {
    async function fetchLabDetails() {
      try {
        setBoardLoading(true);
        setStage(0);
        const data = await fetchLabAPI(labID);
        setLab(data);

        const step = data.status;
        const stepIndex =
          statusToStepMapping[step] ?? statusToStepMapping.default;

        setLabCompletedTillStep(stepIndex);
        setContentStep(stepIndex);

        if (["Review", "Published", "Notebook Review"].includes(step)) {
          setLabGeneration(true);
        }

        setLabDetails(data);
        setLabName(data.lab_name);
      } catch (error) {
        console.error("Error in fetchLabDetails:", error);
        showNotification("Error fetching lab details", "danger");
      } finally {
        setBoardLoading(false);
      }
    }
    fetchLabDetails();
  }, [labID]);

  // Auth
  useEffect(() => {
    async function fetchUser() {
      try {
        const u = await Auth.currentAuthenticatedUser();
        setUser(u);
      } catch (error) {
        console.error("Error in fetchUser:", error);
        showNotification("Error fetching user details", "danger");
      }
    }
    fetchUser();
  }, [showNotification]);

  // WS for long-running tasks
  useEffect(() => {
    if (!user || !lab?._id) return;
    if (
      ![
        "In Lab Generation Queue",
        "In Notebook Generation Queue",
        "In Streamlit Generation Queue",
      ].includes(lab.status)
    ) {
      return;
    }
    const wsUrl = `${process.env.REACT_APP_FASTAPI_URL.replace(
      "http",
      "ws"
    )}/ws/tasks/${user.username}/${lab._id}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => console.log("Task WebSocket connected");
    ws.onmessage = (event) => {
      if (event.data === "Done") {
        window.location.reload();
      } else {
        setInBetweenStagesLoaderMessage("Failed");
        setLab((prev) => ({ ...prev, status: "Failed" }));
        window.location.reload();
      }
    };
    ws.onclose = () => console.log("Task WebSocket disconnected");

    return () => ws.close();
  }, [user, lab?._id, lab?.status]);

  // Hide content loader again whenever backend status changes
  useEffect(() => {
    setSuppressContentOnly(false);
  }, [lab.status]);

  const handleStepClick = (uiIndex) => {
    const isIntermediate = stepperInBetween && uiIndex === baseStepIndex + 1;

    if (isIntermediate) {
      // Clicking the loading bubble restores the content loader
      setSuppressContentOnly(false);
      return;
    }

    // Map UI index to real content index (skip the inserted loading bubble)
    const realIndex =
      stepperInBetween && uiIndex > baseStepIndex ? uiIndex - 1 : uiIndex;

    // ✅ Allow any step up to what’s completed
    const canAccess = realIndex <= labCompletedTillStep;
    if (!canAccess) {
      const message =
        labSteps[realIndex]?.lockedMessage || "This step is locked.";
      showNotification(message, "danger");
      return;
    }

    // If loading and user is going to a previous/at step, hide only the content loader
    if (isInBetween && realIndex <= baseStepIndex) {
      setSuppressContentOnly(true);
    }

    setContentStep(realIndex);
  };

  // Content
  const IntermediateContent = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
      flexDirection="column"
      p={2}
    >
      <CircularProgress className={classes.loaderClass} />
      {/* BIG: expected time */}
      <Typography variant="h6" style={{ marginTop: 16, textTransform: "none" }}>
        {WAIT_MSG || "Working on it…"}
      </Typography>
      {/* SMALL: specific stage message */}
      {inBetweenStagesLoaderMessage && (
        <Typography
          variant="body2"
          style={{ marginTop: 8, opacity: 0.8, textTransform: "none" }}
        >
          {inBetweenStagesLoaderMessage}
        </Typography>
      )}
    </Box>
  );

  const renderContent = () => {
    if (contentInBetween) return <IntermediateContent />;

    const stepLabel = labSteps[contentStep]?.label;
    if (!stepLabel) return <></>;

    switch (stepLabel) {
      case "Lab Definition":
        return (
          <UploadSourceFile
            labID={labID}
            setLabName={setLabName}
            setActiveStep={setContentStep}
          />
        );
      case "Lab Design":
        return (
          <LabDesignStep
            labID={labID}
            setStage={setStage}
            setActiveStep={setContentStep}
            designType="notebook"
          />
        );
      case "Idea Selection":
        return <LabIdea labID={labID} setActiveStep={setContentStep} />;

      case "QuResearch Specifications":
      case "Technical Specifications":
      case "Lab Generation Failed":
      case "Notebook Generation Failed":
      case "Notebook Specifications":
      case "Notebook Specifications Review":
        return (
          <LabUseCase
            projectID={labID}
            projectType="lab_designer"
            setActiveStep={setContentStep}
            useCase="notebook"
          />
        );

      case "Notebook Review":
        return <NotebookReview labID={labID} lab={lab} />;

      case "Streamlit Lab Design":
        return (
          <LabDesignStep
            labID={labID}
            setStage={setStage}
            setActiveStep={setContentStep}
            designType="streamlit"
          />
        );

      case "Streamlit Specifications Review":
      case "Streamlit Specifications":
        return (
          <LabUseCase
            projectID={labID}
            projectType="lab_designer"
            setActiveStep={setContentStep}
            useCase="streamlit"
          />
        );

      case "Review":
      case "Final Deliverables":
        return (
          <ReviewLabProject
            projectID={labID}
            projectType="lab_designer"
            labDetails={labDetails}
            setActiveStep={setContentStep}
            lab={lab}
            setLab={setLab}
            labGeneration={labGeneration}
          />
        );

      default:
        return <></>;
    }
  };

  const handleUpdateLabDetails = async (updatedData) => {
    try {
      setBoardLoading(true);
      const formData = new FormData();
      formData.append("lab_id", lab._id);
      formData.append("lab_name", updatedData.title);
      formData.append("lab_description", updatedData.shortDescription);
      await updateLabDetails(formData);
      setLabName(updatedData.title);
      setLab({
        ...lab,
        lab_name: updatedData.title,
        lab_description: updatedData.shortDescription,
      });
      setBoardLoading(false);
      showNotification("Lab details updated successfully", "success");
    } catch (error) {
      showNotification("Error updating lab details", "danger");
    } finally {
    }
  };

  if (boardLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress className={classes.loaderClass} />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          Loading Lab Board...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Divider style={{ marginBottom: 16 }} />
      <HeadingWithDescription
        title={labName}
        shortDescription={lab.lab_description}
        identifier="lab"
        dialogTitle="Edit Lab Details"
        onChange={handleUpdateLabDetails}
      />

      <AppBar
        position="static"
        className={classes.header}
        style={{
          border: "2px solid #20677b",
          borderRadius: "6px",
          margin: 0,
          marginTop: -16,
          padding: 0,
          minHeight: "40px",
        }}
      >
        <Toolbar style={{ minHeight: "40px", padding: "4px 8px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Stepper
              activeStep={visualActiveStep}
              connector={<QontoConnector />}
              size="small"
              className={classes.stepperRoot}
              style={{
                flexGrow: 1,
                margin: 0,
                padding: "4px 0",
              }}
            >
              {stepsWithIntermediate.map((step, index) => {
                const isIntermediate = !!step.isIntermediate;
                const isDisabled = isIntermediate
                  ? false
                  : index >
                    (stepperInBetween
                      ? labCompletedTillStep + 1
                      : labCompletedTillStep);

                return (
                  <Step
                    key={`${step.label}-${index}`}
                    onClick={() => handleStepClick(index)}
                    style={{ padding: "0 4px" }}
                  >
                    <StepLabel
                      StepIconComponent={
                        isIntermediate ? LoadingStepIcon : undefined
                      }
                      StepIconProps={
                        !isIntermediate && !isDisabled
                          ? {
                              classes: {
                                root: classes.stepIcon,
                                active: classes.activeStepIcon,
                                completed: classes.completedStepIcon,
                              },
                            }
                          : {}
                      }
                      sx={{
                        "& .MuiStepLabel-label": {
                          display: "none",
                        },
                        "&:hover .MuiStepLabel-label": {
                          display: "block",
                          position: "absolute",
                          backgroundColor: "#20677b",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          marginTop: "8px",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        },
                        "& .MuiStepLabel-iconContainer": {
                          paddingRight: 0,
                        },
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      <Box mt={3}>{renderContent()}</Box>
    </Box>
  );
}
